import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useGomakeTheme } from "@/hooks/use-gomake-thme";
import { FONT_FAMILY } from "@/utils/font-family";

const useOutputsAdminModalStyle = () => {
  const { t } = useTranslation();
  const { primaryColor, neutralColor, successColor, errorColor } = useGomakeTheme();

  const classes = useMemo(
    () => ({
      modalInside: {
        width: 640,
        maxWidth: "95vw",
        height: "auto",
        maxHeight: "92vh",
        paddingTop: 24,
        paddingBottom: 0,
        display: "flex",
        flexDirection: "column" as const,
        overflow: "hidden" as const,
      },
      modalContent: {
        display: "flex",
        flexDirection: "column" as const,
        flex: 1,
        minHeight: 0,
        overflow: "hidden" as const,
        height: "100%",
        maxHeight: "none",
      },
      modalBody: {
        flex: 1,
        minHeight: 0,
        overflowY: "auto" as const,
        paddingRight: 4,
        paddingBottom: 8,
      },
      modalTitleStyle: {
        ...FONT_FAMILY.Lexend(600, 22),
        color: primaryColor(900),
      },
      section: {
        display: "flex",
        flexDirection: "column" as const,
        gap: 12,
        padding: 16,
        borderRadius: 12,
        border: `1px solid ${neutralColor(200)}`,
        backgroundColor: neutralColor(50),
      },
      sectionTitle: {
        ...FONT_FAMILY.Lexend(600, 14),
        color: primaryColor(900),
        letterSpacing: "0.02em",
        textTransform: "uppercase" as const,
      },
      sectionHint: {
        ...FONT_FAMILY.Lexend(400, 12),
        color: neutralColor(600),
        marginTop: -4,
      },
      fieldLabel: {
        ...FONT_FAMILY.Lexend(500, 13),
        color: primaryColor(800),
        marginBottom: 6,
      },
      fieldRequired: {
        color: errorColor(500),
        marginLeft: 2,
      },
      textInput: {
        width: "100%",
        borderRadius: 8,
        height: 42,
        backgroundColor: "#FFF",
      },
      selectControl: {
        backgroundColor: "#FFF",
        borderRadius: 8,
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: neutralColor(300),
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: primaryColor(400),
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: primaryColor(500),
        },
      },
      twoColumnGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        gap: 12,
      },
      optionsRow: {
        display: "flex",
        flexDirection: "column" as const,
        gap: 4,
        padding: "8px 12px",
        borderRadius: 8,
        backgroundColor: "#FFF",
        border: `1px solid ${neutralColor(200)}`,
      },
      footer: {
        display: "flex",
        justifyContent: "flex-end",
        gap: 10,
        flexShrink: 0,
        padding: "16px 0 24px",
        borderTop: `1px solid ${neutralColor(200)}`,
        backgroundColor: "#FFF",
      },
      btn: {
        minWidth: 100,
        height: 42,
        borderRadius: 8,
      },
      translationRow: {
        display: "grid",
        gridTemplateColumns: "88px 1fr 36px",
        gap: 8,
        alignItems: "center",
      },
      translationList: {
        display: "flex",
        flexDirection: "column" as const,
        gap: 8,
        maxHeight: 220,
        overflowY: "auto" as const,
        paddingRight: 4,
      },
      langBadge: {
        ...FONT_FAMILY.Lexend(600, 11),
        color: primaryColor(700),
        backgroundColor: primaryColor(50),
        border: `1px solid ${primaryColor(200)}`,
        borderRadius: 6,
        padding: "2px 8px",
        textTransform: "uppercase" as const,
        width: "fit-content",
      },
      addTranslationBtn: {
        alignSelf: "flex-start",
        paddingLeft: 0,
        ...FONT_FAMILY.Lexend(500, 13),
      },
      detailSection: {
        display: "flex",
        flexDirection: "column" as const,
        gap: 10,
        padding: 16,
        borderRadius: 12,
        border: `1px solid ${neutralColor(200)}`,
        backgroundColor: "#FFF",
      },
      detailRow: {
        display: "grid",
        gridTemplateColumns: "140px 1fr",
        gap: 12,
        alignItems: "start",
        padding: "6px 0",
        borderBottom: `1px solid ${neutralColor(100)}`,
      },
      detailRowLast: {
        borderBottom: "none",
      },
      detailLabel: {
        ...FONT_FAMILY.Lexend(500, 13),
        color: neutralColor(600),
      },
      detailValue: {
        ...FONT_FAMILY.Lexend(500, 14),
        color: primaryColor(900),
        wordBreak: "break-word" as const,
      },
      statusActive: {
        ...FONT_FAMILY.Lexend(500, 12),
        color: successColor(700),
        backgroundColor: successColor(50),
        border: `1px solid ${successColor(200)}`,
        borderRadius: 20,
        padding: "2px 10px",
        width: "fit-content",
      },
      statusInactive: {
        ...FONT_FAMILY.Lexend(500, 12),
        color: errorColor(700),
        backgroundColor: errorColor(50),
        border: `1px solid ${errorColor(200)}`,
        borderRadius: 20,
        padding: "2px 10px",
        width: "fit-content",
      },
      translationChip: {
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "6px 10px",
        borderRadius: 8,
        backgroundColor: neutralColor(50),
        border: `1px solid ${neutralColor(200)}`,
        ...FONT_FAMILY.Lexend(400, 13),
        color: primaryColor(900),
      },
      loadingWrap: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "48px 0",
      },
      emptyText: {
        ...FONT_FAMILY.Lexend(400, 14),
        color: neutralColor(600),
        padding: "16px 0",
        textAlign: "center" as const,
      },
    }),
    [t, primaryColor, neutralColor, successColor, errorColor]
  );

  return { classes };
};

export { useOutputsAdminModalStyle };
